/**
 * SIMO-UI-MOBILE-RESPONSIVE-AND-ESCAPE-PASS-1 — ConfirmDialog.
 *
 * Loest F-10 (`window.confirm` mitten in cinematic UI) zentral:
 * konsistenter Two-Button-Dialog gegen Modal-Backdrop, baut auf {@link Modal}.
 * Mit `variant="danger"` rot statt blau (irreversible Aktionen).
 *
 * Beispiel:
 *   <ConfirmDialog
 *     open={!!confirmDelete}
 *     onClose={() => setConfirmDelete(null)}
 *     title="Konversation loeschen?"
 *     confirmLabel="Loeschen"
 *     variant="danger"
 *     busy={busy}
 *     busyLabel="Loesche..."
 *     onConfirm={() => void doDelete(confirmDelete!)}
 *   >
 *     <p className="text-sm mb-5">
 *       Endgueltig, nicht rueckgaengig zu machen.
 *     </p>
 *   </ConfirmDialog>
 */

import { useId } from "react";
import type { ReactNode } from "react";
import { ActionButton } from "../design-system/core.js";
import { Modal } from "./Modal.js";

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  /** Titel des Dialogs. */
  title: string;
  /** Body-Inhalt (Beschreibung, Details, Zusatzinfos). */
  children?: ReactNode;
  /** Label fuer den Confirm-Button. Default "Bestaetigen". */
  confirmLabel?: string;
  /** Label fuer den Cancel-Button. Default "Abbrechen". */
  cancelLabel?: string;
  /** Optisches Gewicht des Confirm-Buttons. Default `primary`. */
  variant?: "primary" | "danger";
  /** Wenn `true`: Button disabled + zeigt `busyLabel`. */
  busy?: boolean;
  /** Label waehrend `busy`. Default = `confirmLabel`. */
  busyLabel?: string;
  /** Callback bei Confirm. Schliesst NICHT automatisch — Caller entscheidet. */
  onConfirm: () => void;
}

export function ConfirmDialog({
  open,
  onClose,
  title,
  children,
  confirmLabel = "Bestaetigen",
  cancelLabel = "Abbrechen",
  variant = "primary",
  busy,
  busyLabel,
  onConfirm,
}: ConfirmDialogProps): JSX.Element {
  const titleId = useId();
  const descriptionId = useId();
  // Esc-Cleanup waehrend laufender Operation: User soll nicht versehentlich
  // mit Esc abbrechen, wenn busy=true (z.B. Cherry-Pick laeuft). Backdrop
  // bleibt fuer denselben Grund deaktiviert.
  return (
    <Modal
      open={open}
      onClose={onClose}
      titleId={titleId}
      descriptionId={children ? descriptionId : undefined}
      dismissOnEscape={!busy}
      dismissOnBackdrop={!busy}
    >
      <h3 id={titleId} className="text-lg font-semibold mb-3">
        {title}
      </h3>
      {children && <div id={descriptionId} className="mb-5">{children}</div>}
      <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
        <ActionButton onClick={onClose} disabled={busy} autoFocus={variant === "danger"}>
          {cancelLabel}
        </ActionButton>
        <ActionButton
          variant={variant === "danger" ? "danger" : "primary"}
          disabled={busy}
          onClick={onConfirm}
          autoFocus={variant !== "danger"}
          aria-busy={busy || undefined}
        >
          {busy ? (busyLabel ?? confirmLabel) : confirmLabel}
        </ActionButton>
      </div>
    </Modal>
  );
}
